import { MetricsBoxPlot } from "@/components/metrics-box-plot";
import { formatValue, valuesMatch } from "@/domain/calibration-params";
import {
  heatFill,
  heatScale,
  matrixRow,
  METRICS_KINDS,
  type HeatScale,
  type MetricDisplaySpec,
  type MetricParamValue,
  type MetricsKind,
} from "@/domain/metrics-params";
import { cn } from "@/lib/utils";

const METRICS_ACCURACY_FALLBACK = 4;

interface ValueRow {
  key: string;
  label?: string;
  values: (number | null)[];
  compare?: (number | null)[];
  /** Heat level per column, parallel to values; null leaves a cell unpainted. */
  heat?: (number | null)[];
  accuracy?: number;
  chpDataType?: string;
}

/** One table of metric vectors: numbered value columns, optional row-label
 * column, and a change marker per cell where a compare row differs — amber on
 * plain cells, but a ring on shaded ones, where amber all but disappears
 * against the fill in light mode. */
function MetricsValueTable({
  cols,
  labelHeader,
  rows,
}: {
  cols: number;
  labelHeader?: string;
  rows: ValueRow[];
}) {
  const hasLabels = rows.some((r) => r.label != null);
  const hasHeat = rows.some((r) => r.heat != null);
  return (
    <div className="overflow-x-auto">
      <table
        className={cn("text-xs border-separate", hasHeat ? "border-spacing-[2px]" : "border-spacing-0")}
      >
        <thead>
          <tr>
            {hasLabels && (
              <th className="py-0.5 pr-3 text-left font-normal text-muted-foreground">
                {labelHeader}
              </th>
            )}
            {Array.from({ length: cols }).map((_, col) => (
              <th key={col} className="px-2 py-0.5 text-right font-normal text-muted-foreground">
                {col + 1}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => {
            const accuracy = row.accuracy ?? METRICS_ACCURACY_FALLBACK;
            const rowName = row.label ?? `Row ${rowIndex + 1}`;
            return (
              <tr key={row.key}>
                {hasLabels && (
                  <td className="py-0.5 pr-3 whitespace-nowrap text-muted-foreground">
                    {row.label}
                  </td>
                )}
                {Array.from({ length: cols }).map((_, col) => {
                  const value = row.values[col];
                  const previous = row.compare?.[col];
                  const changed =
                    row.compare != null &&
                    !valuesMatch(value, previous, accuracy, row.chpDataType);
                  const level = row.heat?.[col] ?? null;
                  const shown =
                    value != null ? formatValue(value, accuracy, row.chpDataType) : "-";
                  return (
                    <td
                      key={col}
                      style={level != null ? { backgroundColor: heatFill(level) } : undefined}
                      title={
                        // Numbered headers on a dense grid make it easy to lose
                        // which cell you are on; no Axes metadata to name them.
                        row.heat != null
                          ? `${rowName} × ${col + 1}: ${shown}` +
                            (changed && previous != null
                              ? ` (was ${formatValue(previous, accuracy, row.chpDataType)})`
                              : "")
                          : undefined
                      }
                      className={cn(
                        "px-2 py-0.5 text-right font-mono tabular-nums whitespace-nowrap",
                        level != null && "rounded-sm text-foreground",
                        changed
                          ? level != null
                            ? "ring-1 ring-inset ring-foreground/50 font-semibold"
                            : "text-amber-600 dark:text-amber-400 font-medium bg-amber-50 dark:bg-amber-950/40 rounded"
                          : level == null && "text-foreground/80",
                      )}
                    >
                      {shown}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Scale key for a shaded matrix: the run's own best and worst cell, so the
 * shading is never mistaken for a verdict against a fixed limit. */
function HeatLegend({
  scale,
  accuracy,
  chpDataType,
}: {
  scale: HeatScale;
  accuracy?: number;
  chpDataType?: string;
}) {
  const format = (v: number) =>
    formatValue(v, accuracy ?? METRICS_ACCURACY_FALLBACK, chpDataType);
  return (
    <div className="flex items-center gap-1.5 text-[10px] leading-none text-muted-foreground mb-1">
      <span>
        best <span className="tabular-nums">{format(scale.best)}</span>
      </span>
      <span
        aria-hidden
        className="h-1.5 w-10 rounded-full"
        style={{ background: `linear-gradient(to right, ${heatFill(0)}, ${heatFill(1)})` }}
      />
      <span>
        <span className="tabular-nums">{format(scale.worst)}</span> worst
      </span>
    </div>
  );
}

/** One matrix-shaped metric (6x6, 2x6 or the 5x4 quantiles): NVP serves the
 * value as a flat column-major vector; the semantic shape comes from the
 * display spec. A spec carrying a plot draws it above its own numbers, which
 * stay on the page as the recording's evidence. */
export function MetricsMatrix({
  spec,
  param,
  compare,
}: {
  spec: MetricDisplaySpec;
  param: MetricParamValue;
  compare?: MetricParamValue;
}) {
  const scale = heatScale(param.values, spec);
  const rows: ValueRow[] = Array.from({ length: spec.rows }, (_, row) => ({
    key: String(row),
    label: spec.rowLabels?.[row],
    values: matrixRow(param.values, spec, row),
    compare: compare ? matrixRow(compare.values, spec, row) : undefined,
    heat: scale
      ? Array.from({ length: spec.cols }, (_, col) => scale.at(row, col))
      : undefined,
    accuracy: param.accuracy,
    chpDataType: param.chpDataType,
  }));
  return (
    <div className="min-w-0">
      <div className="text-xs font-medium text-muted-foreground mb-1">{spec.label}</div>
      {scale && (
        <HeatLegend scale={scale} accuracy={param.accuracy} chpDataType={param.chpDataType} />
      )}
      {spec.plot === "box" && (
        <MetricsBoxPlot spec={spec} param={param} compare={compare} />
      )}
      <MetricsValueTable cols={spec.cols} rows={rows} />
    </div>
  );
}

/** All of one recording's params: matrices individually, 1xN rows consolidated
 * into a single table with one row per metric. */
export function RecordingGrids({
  kind,
  params,
  compare,
}: {
  kind: MetricsKind;
  params: Map<string, MetricParamValue>;
  compare?: Map<string, MetricParamValue>;
}) {
  const spec = METRICS_KINDS[kind];
  const matrixParams = spec.params.filter((p) => p.spec.rows > 1);
  const quadParams = spec.params.filter((p) => p.spec.rows === 1);

  const quadRows: ValueRow[] = quadParams.flatMap(({ name, spec: s }) => {
    const param = params.get(name);
    if (!param) return [];
    const previous = compare?.get(name);
    return [
      {
        key: name,
        label: s.label,
        values: matrixRow(param.values, s, 0),
        compare: previous ? matrixRow(previous.values, s, 0) : undefined,
        accuracy: param.accuracy,
        chpDataType: param.chpDataType,
      },
    ];
  });

  return (
    <div className="space-y-4">
      {quadRows.length > 0 && (
        <MetricsValueTable
          cols={quadParams[0].spec.cols}
          labelHeader="Metric"
          rows={quadRows}
        />
      )}
      {matrixParams.length > 0 && (
        <div className="flex flex-wrap gap-x-8 gap-y-4">
          {matrixParams.map(({ name, spec: s }) => {
            const param = params.get(name);
            if (!param) return null;
            return (
              <MetricsMatrix key={name} spec={s} param={param} compare={compare?.get(name)} />
            );
          })}
        </div>
      )}
    </div>
  );
}
